import { useState } from "react";
import { X, Star, MessageSquareQuote, Loader2, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { friendlyError } from "@/lib/errors";
import { useAuth } from "@/lib/auth";
import { safeStorage } from "@/lib/storage";

/**
 * Prompts the user to leave a testimonial at key moments (case filed +
 * approval received). Persists dismissal per-case-per-moment in localStorage
 * so we don't nag people forever.
 */
export function TestimonialPrompt({ caseId, moment, defaultRelationship }) {
  const { user } = useAuth();
  const storageKey = `gcu:testimonial-prompt:${caseId}:${moment}`;
  const [dismissed, setDismissed] = useState(() => Boolean(safeStorage.get(storageKey)));
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (dismissed || submitted) return null;

  const dismiss = () => {
    safeStorage.set(storageKey, "1");
    setDismissed(true);
  };

  const prompts = {
    filed: {
      title: "Nice work — your packet is off to USCIS! 🎉",
      body: "Would you share a quick note about how the preparation went? Complimentary quotes may be featured on our homepage.",
      cta: "Leave a note",
    },
    decided: {
      title: "Congratulations on your approval! 🎉",
      body: "We'd love to hear how DS-260USA.com helped. Approved testimonials appear on our homepage to help other families.",
      cta: "Share my story",
    },
  };
  const p = prompts[moment] || prompts.filed;

  return (
    <>
      <div
        className="card-surface p-5 mb-5 flex items-start justify-between gap-4"
        style={{
          background: "linear-gradient(135deg, #FDF8F0 0%, #FFFFFF 60%)",
          border: "1px solid var(--ai-border)",
        }}
        data-testid={`testimonial-prompt-${moment}`}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center"
               style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-border)" }}>
            <MessageSquareQuote className="w-5 h-5" style={{ color: "var(--ai-text)" }} />
          </div>
          <div>
            <div className="font-display font-bold text-sm mb-1" style={{ color: "var(--text-primary)" }}>
              {p.title}
            </div>
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
              {p.body}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button type="button" onClick={dismiss}
                  className="btn-ghost text-xs"
                  data-testid={`testimonial-dismiss-${moment}`}>
            Not now
          </button>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="btn-primary text-xs"
            data-testid={`testimonial-open-${moment}`}
          >
            {p.cta}
          </button>
        </div>
      </div>

      {open && (
        <TestimonialModal
          moment={moment}
          caseId={caseId}
          defaultName={user?.name || ""}
          defaultRelationship={defaultRelationship}
          onClose={() => setOpen(false)}
          onSubmitted={() => {
            setSubmitted(true);
            dismiss();
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

export function TestimonialModal({ moment, caseId, defaultName, defaultRelationship, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    display_name: defaultName || "",
    city: "",
    state: "",
    relationship: defaultRelationship || "petitioning for my spouse",
    rating: 5,
    quote: "",
    consent_public: true,
  });
  const [busy, setBusy] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/testimonials", {
        case_id: caseId,
        prompt_moment: moment,
        display_name: form.display_name.trim(),
        city: form.city.trim() || null,
        state: form.state.trim().toUpperCase() || null,
        relationship: form.relationship.trim() || null,
        rating: form.rating,
        quote: form.quote.trim(),
        consent_public: form.consent_public,
      });
      toast.success("Thank you — your note is under review.");
      onSubmitted?.();
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't save your note. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(26,46,36,0.55)" }}
      onClick={onClose}
      data-testid="testimonial-modal"
    >
      <div className="card-surface p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-display font-bold text-lg" style={{ color: "var(--text-primary)" }}>
            Share your experience
          </h3>
          <button type="button" onClick={onClose} className="btn-ghost">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="field-label">Your name (public)</label>
              <input className="field-input" value={form.display_name}
                     onChange={(e) => set("display_name")(e.target.value)}
                     data-testid="testimonial-name"
                     placeholder="Maria R. or first name only" />
            </div>
            <div>
              <label className="field-label">Relationship</label>
              <input className="field-input" value={form.relationship}
                     onChange={(e) => set("relationship")(e.target.value)}
                     data-testid="testimonial-relationship"
                     placeholder="petitioning for my spouse" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="field-label">City</label>
              <input className="field-input" value={form.city}
                     onChange={(e) => set("city")(e.target.value)}
                     data-testid="testimonial-city"
                     placeholder="Austin" />
            </div>
            <div>
              <label className="field-label">State</label>
              <input className="field-input" value={form.state}
                     onChange={(e) => set("state")(e.target.value.toUpperCase().slice(0, 2))}
                     data-testid="testimonial-state" maxLength={2} placeholder="TX" />
            </div>
          </div>
          <div>
            <label className="field-label">Rating</label>
            <StarPicker value={form.rating} onChange={set("rating")} />
          </div>
          <div>
            <label className="field-label">Your note (15–600 chars)</label>
            <textarea className="field-input text-sm" rows={5} value={form.quote}
                      onChange={(e) => set("quote")(e.target.value)}
                      data-testid="testimonial-quote"
                      placeholder="What was helpful? What would you tell a friend who's starting their case?" />
            <div className="text-[10.5px] mt-1 text-right"
                 style={{ color: form.quote.length > 600 ? "#991B1B" : "var(--text-secondary)" }}>
              {form.quote.length}/600
            </div>
          </div>
          <label className="flex items-start gap-2 text-xs cursor-pointer"
                 style={{ color: "var(--text-primary)" }}>
            <input type="checkbox" checked={form.consent_public}
                   onChange={(e) => set("consent_public")(e.target.checked)}
                   data-testid="testimonial-consent" className="mt-0.5" />
            <span>
              I consent to my name (as entered above), city/state, and note being displayed
              publicly on the DS-260USA.com homepage if approved by an administrator.
            </span>
          </label>
        </div>

        <div className="mt-5 flex items-center gap-2 justify-end">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          <button
            type="button"
            className="btn-primary text-sm"
            disabled={busy || form.quote.trim().length < 15 || !form.display_name.trim() || !form.consent_public}
            onClick={submit}
            data-testid="testimonial-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : <ThumbsUp className="w-4 h-4 inline mr-1" />}
            Submit for review
          </button>
        </div>
        <p className="text-[10.5px] mt-3 italic" style={{ color: "var(--text-secondary)" }}>
          An administrator reviews every submission before it&apos;s published. You can ask us to remove your note at any time.
        </p>
      </div>
    </div>
  );
}

function StarPicker({ value, onChange }) {
  return (
    <div className="flex items-center gap-1" data-testid="testimonial-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className="p-1"
          data-testid={`star-${n}`}
          aria-label={`${n} star${n === 1 ? "" : "s"}`}
        >
          <Star
            className="w-6 h-6"
            style={{
              color: n <= value ? "#E5B93B" : "#E5E3DC",
              fill: n <= value ? "#F6C244" : "transparent",
            }}
          />
        </button>
      ))}
    </div>
  );
}
